// İletişim (2026-10-07) — Mesajlar, Telefon Görüşmeleri ve Uygulama Bildirimleri tek
// sekmede; üstte bölüm seçici. Mesajlaşma Mesajlar sayfasına yönlendirmeden burada
// yapılır (hasta başlığındaki "Mesaj gönder" kaldırıldı).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { COLORS } from "../../../styles/uiConstants";
import { SegmentedControl } from "../../atoms";
import { useClinicTheme } from "../../../theme";
import SummaryTile from "../../molecules/SummaryTile";
import { useMessageThreadQuery } from "../../../store/messageApi";
import {
  useListPhoneCallsByPatientMutation,
  useListNotificationSettingsByPatientMutation,
  useListNotificationLogsByPatientMutation,
} from "../../../store/secretaryApi";
import { relativePast } from "../../../pages/Secretary/PatientsPage/patientListFormat";
import PatientMessagesPanel from "./PatientMessagesPanel";
import PhoneCallTab from "./PhoneCallTab";
import NotificationTab from "./NotificationTab";

export const COMMUNICATION_SECTIONS = [
  { value: "mesajlar", label: "Mesajlar", needs: "messages" },
  { value: "telefon", label: "Telefon Görüşmeleri", needs: "phoneCalls" },
  { value: "bildirimler", label: "Uygulama Bildirimleri", needs: "notifications" },
];

/** Eski ayrı sekmelerin adresleri → yeni bölüm (eski bağlantılar çalışsın). */
export const LEGACY_COMMUNICATION_TABS = { telefon: "telefon", bildirimler: "bildirimler" };

/**
 * @param {{messages: boolean, phoneCalls: boolean, notifications: boolean}} can - bölüm görünürlüğü
 * @param {string|null} section - URL'deki bölüm (?bolum=)
 */
export default function CommunicationTab({ patientId, token, canEdit, can, section, onSectionChange }) {
  const { palette } = useClinicTheme();
  const sections = useMemo(
    () => COMMUNICATION_SECTIONS.filter((s) => can[s.needs]),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [can.messages, can.phoneCalls, can.notifications]
  );
  const current = sections.some((s) => s.value === section) ? section : sections[0]?.value;

  // ── Özet kutuları (masaüstü) ──
  // Mesaj akışı Mesajlar bölümüyle aynı argümanlarla → önbellek ortak, ek istek yok.
  const { currentData: threadRes, isLoading: threadLoading } = useMessageThreadQuery(
    { token, patientId, limit: 40 },
    { skip: !token || !patientId || !can.messages }
  );
  const conv = threadRes?.data?.conversation || null;
  const [listCalls] = useListPhoneCallsByPatientMutation();
  const [listSettings] = useListNotificationSettingsByPatientMutation();
  const [listLogs] = useListNotificationLogsByPatientMutation();
  const [summary, setSummary] = useState(null);

  // Bölüm değişince tazelenir (içerikte kayıt eklenmiş olabilir)
  const loadSummary = useCallback(async () => {
    if (!token || !patientId) return;
    const safe = (p) => p.then((r) => r?.data?.list || []).catch(() => null);
    const [calls, settings, logs] = await Promise.all([
      can.phoneCalls ? safe(listCalls({ token, patientId }).unwrap()) : null,
      can.notifications ? safe(listSettings({ token, patientId }).unwrap()) : null,
      can.notifications ? safe(listLogs({ token, patientId }).unwrap()) : null,
    ]);
    setSummary({ calls, settings, logs });
  }, [token, patientId, can.phoneCalls, can.notifications, listCalls, listSettings, listLogs]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary, current]);

  const newest = (list, field) =>
    (list || []).reduce((best, x) => {
      const t = new Date(x?.[field] || x?.createdAt || 0).getTime();
      return t > best ? t : best;
    }, 0);
  const lastCall = newest(summary?.calls, "date");
  const lastLog = newest(summary?.logs, "sentAt");

  const tiles = [
    can.messages && {
      key: "mesajlar",
      label: "Mesajlar",
      loading: threadLoading && !threadRes,
      value: conv?.unread
        ? `${conv.unread} okunmamış mesaj`
        : conv?.lastMessageAt
          ? `Son mesaj ${relativePast(conv.lastMessageAt).toLocaleLowerCase("tr-TR")}`
          : "Henüz mesaj yok",
      hint: conv?.lastMessagePreview || (canEdit ? "Yazmak için tıklayın" : ""),
    },
    can.phoneCalls && {
      key: "telefon",
      label: "Telefon görüşmeleri",
      loading: !summary,
      value: summary?.calls?.length ? `${summary.calls.length} görüşme` : "Kayıt yok",
      hint: lastCall ? `Son görüşme ${relativePast(lastCall).toLocaleLowerCase("tr-TR")}` : "",
    },
    can.notifications && {
      key: "bildirimler",
      label: "Uygulama bildirimleri",
      loading: !summary,
      value: summary?.settings?.length ? `${summary.settings.length} bildirim tanımlı` : "Tanımlı bildirim yok",
      hint: lastLog ? `Son gönderim ${relativePast(lastLog).toLocaleLowerCase("tr-TR")}` : "",
    },
  ].filter(Boolean);

  useEffect(() => {
    if (current && current !== section) onSectionChange(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 1.5, md: 2 }, flexShrink: 0, borderBottom: `1px solid ${COLORS.slate200}` }}>
        {/* Masaüstü: özet kutuları seçici görevi görür. Mobil: yalnız bölüm seçici (ikisi birlikte değil). */}
        <Box
          sx={{
            display: { xs: "none", md: "grid" },
            gap: 1.5,
            gridTemplateColumns: { md: `repeat(${tiles.length}, minmax(0, 1fr))` },
          }}
        >
          {tiles.map((t) => (
            <SummaryTile key={t.key} {...t} active={current === t.key} onClick={() => onSectionChange(t.key)} palette={palette} />
          ))}
        </Box>
        {sections.length > 1 ? (
          <Box sx={{ display: { xs: "block", md: "none" } }}>
            <SegmentedControl
              value={current || ""}
              onChange={onSectionChange}
              options={sections.map(({ value, label }) => ({ value, label }))}
              ariaLabel="İletişim bölümü"
              palette={palette}
            />
          </Box>
        ) : null}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {current === "mesajlar" && <PatientMessagesPanel patientId={patientId} />}
        {current === "telefon" && (
          <PhoneCallTab mode="standalone" patientId={patientId} token={token} canEdit={canEdit} />
        )}
        {current === "bildirimler" && (
          <NotificationTab mode="standalone" patientId={patientId} token={token} canEdit={canEdit} />
        )}
      </Box>
    </Box>
  );
}
